// Daily tracking between sessions (SPEC.md §4.3): fluid, weight, home blood pressure, symptoms and
// access checks, plus the photos attached to access checks.
import { startOfDay, subDays } from 'date-fns';
import { useDbQuery } from '../db/hooks';
import type { DbClient } from '../db/client';
import type { AccessType } from './profile';

export type FluidKind = 'water' | 'tea' | 'soup' | 'other';

export const FLUID_KIND_LABELS: Record<FluidKind, string> = {
  water: 'Water',
  tea: 'Tea or coffee',
  soup: 'Soup',
  other: 'Other'
};

export interface FluidEntry {
  id: string;
  taken_at: string;
  amount_ml: number;
  kind: FluidKind;
  note: string | null;
}

export interface WeightEntry {
  id: string;
  measured_at: string;
  weight_kg: number;
  context: 'morning' | 'other';
}

export interface BpEntry {
  id: string;
  measured_at: string;
  systolic: number;
  diastolic: number;
  pulse: number | null;
}

/** The seeded symptom list (SPEC.md §4.3); anything else is saved as typed. */
export const SYMPTOMS = [
  'Cramps',
  'Fatigue',
  'Itching',
  'Nausea',
  'Headache',
  'Dizziness',
  'Shortness of breath',
  'Swelling',
  'Restless legs',
  'Insomnia',
  'Low appetite',
  'Chest pain'
] as const;

export const SEVERITY_LABELS: Record<number, string> = { 1: 'Very mild', 2: 'Mild', 3: 'Moderate', 4: 'Bad', 5: 'Severe' };

export interface SymptomEntry {
  id: string;
  occurred_at: string;
  symptom: string;
  severity: number;
  during_session_id: string | null;
  note: string | null;
}

/** Yes/no observations on an access check; stored as 0/1, null when not checked. */
export const ACCESS_SIGNS = ['thrill_present', 'bruit_present', 'redness', 'swelling', 'pain', 'bleeding_prolonged', 'discharge', 'fever'] as const;
export type AccessSign = (typeof ACCESS_SIGNS)[number];

export const ACCESS_SIGN_LABELS: Record<AccessSign, string> = {
  thrill_present: 'Thrill (buzz) felt',
  bruit_present: 'Bruit (whoosh) heard',
  redness: 'Redness',
  swelling: 'Swelling',
  pain: 'Pain or tenderness',
  bleeding_prolonged: 'Bleeding that took long to stop',
  discharge: 'Discharge or pus',
  fever: 'Fever or chills'
};

export type AccessCheck = { id: string; checked_at: string; access_type: AccessType | null; note: string | null } & Record<AccessSign, number | null>;

/**
 * Signs on the "tell your care team" list (SPEC.md §5 Health → Access): any problem sign, or no
 * thrill/bruit on a fistula or graft. Compares against the list shown to the user; not a diagnosis.
 */
export function accessWarnings(check: AccessCheck): AccessSign[] {
  const problems = (['redness', 'swelling', 'pain', 'bleeding_prolonged', 'discharge', 'fever'] as const).filter((sign) => check[sign] === 1);
  const flowSigns =
    check.access_type === 'catheter' ? [] : (['thrill_present', 'bruit_present'] as const).filter((sign) => check[sign] === 0);
  return [...flowSigns, ...problems];
}

export interface Attachment {
  id: string;
  owner_table: string;
  owner_id: string;
  mime: string;
  bytes: number;
  file_name: string | null;
  created_at: string;
}

/** Start of the local day `days` ago. Day-rounded so the query key stays stable between renders. */
const since = (days: number) => startOfDay(subDays(new Date(), days)).toISOString();

export function useFluidEntries(fromIso: string, toIso: string) {
  const query = useDbQuery<FluidEntry>(
    'SELECT id, taken_at, amount_ml, kind, note FROM fluid_entries WHERE deleted_at IS NULL AND taken_at >= ? AND taken_at < ? ORDER BY taken_at DESC',
    [fromIso, toIso],
    ['fluid_entries']
  );
  return { entries: query.data ?? [], isLoading: query.isLoading };
}

export function useWeightEntries(days = 365) {
  const query = useDbQuery<WeightEntry>(
    'SELECT id, measured_at, weight_kg, context FROM weight_entries WHERE deleted_at IS NULL AND measured_at >= ? ORDER BY measured_at DESC',
    [since(days)],
    ['weight_entries']
  );
  return { entries: query.data ?? [], isLoading: query.isLoading };
}

export function useBpEntries(days = 365) {
  const query = useDbQuery<BpEntry>(
    'SELECT id, measured_at, systolic, diastolic, pulse FROM bp_entries WHERE deleted_at IS NULL AND measured_at >= ? ORDER BY measured_at DESC',
    [since(days)],
    ['bp_entries']
  );
  return { entries: query.data ?? [], isLoading: query.isLoading };
}

export function useSymptomEntries(days = 365) {
  const query = useDbQuery<SymptomEntry>(
    'SELECT id, occurred_at, symptom, severity, during_session_id, note FROM symptom_entries WHERE deleted_at IS NULL AND occurred_at >= ? ORDER BY occurred_at DESC',
    [since(days)],
    ['symptom_entries']
  );
  return { entries: query.data ?? [], isLoading: query.isLoading };
}

export function useSessionSymptoms(sessionId: string) {
  const query = useDbQuery<SymptomEntry>(
    'SELECT id, occurred_at, symptom, severity, during_session_id, note FROM symptom_entries WHERE deleted_at IS NULL AND during_session_id = ? ORDER BY occurred_at',
    [sessionId],
    ['symptom_entries']
  );
  return query.data ?? [];
}

export function useAccessChecks(days = 365) {
  const query = useDbQuery<AccessCheck>(
    `SELECT id, checked_at, access_type, ${ACCESS_SIGNS.join(', ')}, note FROM access_checks WHERE deleted_at IS NULL AND checked_at >= ? ORDER BY checked_at DESC`,
    [since(days)],
    ['access_checks']
  );
  return { checks: query.data ?? [], isLoading: query.isLoading };
}

export function useAttachments(ownerTable: string) {
  const query = useDbQuery<Attachment>(
    'SELECT id, owner_table, owner_id, mime, bytes, file_name, created_at FROM attachments WHERE deleted_at IS NULL AND owner_table = ? ORDER BY created_at',
    [ownerTable],
    ['attachments']
  );
  const byOwner = new Map<string, Attachment[]>();
  for (const attachment of query.data ?? []) byOwner.set(attachment.owner_id, [...(byOwner.get(attachment.owner_id) ?? []), attachment]);
  return byOwner;
}

/** The most recent entry of a table, used to prefill forms with the last values. */
export function useLastEntry<T extends object>(table: 'fluid_entries' | 'weight_entries' | 'bp_entries' | 'symptom_entries', timeColumn: string) {
  const query = useDbQuery<T>(`SELECT * FROM ${table} WHERE deleted_at IS NULL ORDER BY ${timeColumn} DESC LIMIT 1`, [], [table]);
  return query.data?.[0] ?? null;
}

export type TrackingTable = 'fluid_entries' | 'weight_entries' | 'bp_entries' | 'symptom_entries' | 'access_checks';

/** Creates the entry, or updates it when `id` is given. */
export async function saveEntry<T extends object>(db: DbClient, table: TrackingTable, id: string | undefined, values: Partial<T>) {
  const row = id ? await db.update<T & { id: string }>(table, id, values) : await db.insert<T & { id: string }>(table, values);
  return row;
}

export async function deleteEntry(db: DbClient, table: TrackingTable, id: string) {
  if (table === 'access_checks') {
    const photos = await db.query<{ id: string }>(
      "SELECT id FROM attachments WHERE owner_table = 'access_checks' AND owner_id = ? AND deleted_at IS NULL",
      [id]
    );
    for (const photo of photos) await db.deleteAttachment(photo.id);
  }
  await db.softDelete(table, id);
}

export function addFluid(db: DbClient, amountMl: number, kind: FluidKind = 'water') {
  return db.insert('fluid_entries', { taken_at: new Date().toISOString(), amount_ml: amountMl, kind });
}
